import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiX } from "react-icons/fi";
import { getTrackingData } from "../../utils/trackingConfig";
import { subscriptionData } from "../../redux/slices/subscriptionSlice";
import { subscriptionPayment } from "../../redux/slices/subscriptionPaymentSlice";
// Reuse the upgrade-pro-modal styling — same layout, colours, sizes
// and mobile bottom-sheet behaviour, only the icon / badge / copy change.
import "./ComparisonModal.scss";

const SWIPE_CLOSE_THRESHOLD = 110;
const SWIPE_MOBILE_BREAKPOINT = 576;

/* Icons — same proportions as the standalone "Paywall Modal" set. */
const BuildingIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M3 21V8l9-5 9 5v13" />
    <path d="M9 21v-6h6v6M9 12h.01M15 12h.01M9 8h.01M15 8h.01" />
  </svg>
);

const SparkIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 2 13.6 8.4 20 10l-6.4 1.6L12 18l-1.6-6.4L4 10l6.4-1.6z" />
  </svg>
);

const DEFAULTS = {
  softHeading: "Looks like you're exploring. Want to keep going?",
  infoText: "Pro users see 21,000+ cities instead of 1/day",
  trialPrice: "$2.99/month",
  yearlyPrice: "$29.99/year",
  newsletterPrice: "$4.99/month billed annually at $49.99/year",
  newsletterLabel: "Pro + Newsletter",
  badge: "Daily limit",
};

const headingForPlan = () => "You've used your free city detail view today";

// localStorage key — once set, every future open shows the standard
// "You've used your free city detail view today" headline. The first
// open ever shows the softer "Looks like you're exploring..." copy.
const SEEN_KEY = "mmp_city_detail_limit_seen";

const CityDetailLimitModal = ({
  isVisible,
  onClose,
  onUpgrade,
  eventSource,
  // optional overrides — fall back to the default cityDetail copy
  heading,
  infoText,
  yearlyPrice,
  trialPrice,
  newsletterPrice,
  newsletterLabel,
  showBadge = true,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const subscriptionState = useSelector((state) => state.subscriptionData);
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  // Plan name — used to pick "free" vs "plus" copy in the standard
  // (non-first-open) headline.
  const authStore = useSelector((state) => state.auth);
  const profileStore = useSelector((state) => state.profile);
  const currentPlan =
    authStore?.user?.plan?.name || profileStore?.profileItem?.data?.plan?.name;
  const hasUsedProTrial =
    authStore?.user?.has_used_pro_trial ??
    profileStore?.profileItem?.data?.has_used_pro_trial ??
    false;

  // First-open detection: on the very first open ever (per browser)
  // show the soft "Looks like you're exploring..." headline; every
  // subsequent open uses the plan-aware "You've used your <plan>..." copy.
  const [isFirstOpen, setIsFirstOpen] = useState(false);
  useEffect(() => {
    if (!isVisible) return;
    try {
      const seen = localStorage.getItem(SEEN_KEY) === "1";
      setIsFirstOpen(!seen);
      if (!seen) localStorage.setItem(SEEN_KEY, "1");
    } catch {
      setIsFirstOpen(false);
    }
  }, [isVisible]);

  const finalHeading =
    heading ||
    (isFirstOpen ? DEFAULTS.softHeading : headingForPlan(currentPlan));
  const finalInfo = infoText || DEFAULTS.infoText;
  const finalYearly = yearlyPrice || DEFAULTS.yearlyPrice;
  const finalTrial = trialPrice || DEFAULTS.trialPrice;
  const finalNewsletterPrice = newsletterPrice || DEFAULTS.newsletterPrice;
  const finalNewsletterLabel = newsletterLabel || DEFAULTS.newsletterLabel;

  /* Mobile-only swipe-down-to-close (same gesture as ComparisonModal). */
  const dialogRef = useRef(null);
  const startYRef = useRef(null);
  const draggingRef = useRef(false);
  const [dragY, setDragY] = useState(0);

  const isMobile = () =>
    typeof window !== "undefined" &&
    window.innerWidth <= SWIPE_MOBILE_BREAKPOINT;

  const onTouchStart = (e) => {
    if (!isMobile()) return;
    startYRef.current = e.touches[0].clientY;
    draggingRef.current = true;
  };
  const onTouchMove = (e) => {
    if (!draggingRef.current || startYRef.current == null) return;
    const dy = e.touches[0].clientY - startYRef.current;
    setDragY(dy > 0 ? dy : 0);
  };
  const onTouchEnd = () => {
    if (!draggingRef.current) return;
    const finalDy = dragY;
    draggingRef.current = false;
    startYRef.current = null;
    if (finalDy > SWIPE_CLOSE_THRESHOLD) {
      setDragY(0);
      onClose && onClose();
    } else {
      setDragY(0);
    }
  };

  useEffect(() => {
    if (!isVisible) setDragY(0);
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    const content = document.querySelector(".upgrade-pro-modal .modal-content");
    if (!content) return;
    if (dragY > 0) {
      content.style.transform = `translateY(${dragY}px)`;
      content.style.transition = "none";
    } else {
      content.style.transform = "";
      content.style.transition = "";
    }
  }, [dragY, isVisible]);

  const handleUpgrade = () => {
    onUpgrade && onUpgrade();
    onClose && onClose();
    const { event } = getTrackingData(eventSource);
    navigate("/profile/subscription", { state: { source: eventSource } });
  };

  const handleSkipTrial = async () => {
    onUpgrade && onUpgrade();
    let plans = subscriptionState?.subscriptionDataItems || [];
    if (!plans.length) {
      try {
        const result = await dispatch(subscriptionData()).unwrap();
        plans = result?.subscriptionData || [];
      } catch (e) {
        handleUpgrade();
        return;
      }
    }
    const proMonthlyPlan = plans.find(
      (p) =>
        p.name === "Pro" &&
        p.interval === "month" &&
        p.slug !== "pro-newsletter",
    );
    if (!proMonthlyPlan?.price_id) {
      handleUpgrade();
      return;
    }
    const { event } = getTrackingData(eventSource);
    const payload = {
      priceId: proMonthlyPlan.price_id,
      customerEmail: authStore?.user?.email,
      metadata: {
        userId: authStore?.user?.id,
        plan_id: proMonthlyPlan.id,
        billingCycle: "month",
        trigger_feature: event,
      },
    };
    setIsCheckingOut(true);
    try {
      const result = await dispatch(subscriptionPayment(payload)).unwrap();
      const checkoutUrl =
        result?.subscriptionPaymentItems?.url ||
        result?.url ||
        result?.data?.url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        handleUpgrade();
      }
    } catch (error) {
      console.error("Skip trial checkout failed:", error);
      handleUpgrade();
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleTrial = () => {
    onUpgrade && onUpgrade();
    onClose && onClose();
    navigate("/profile/subscription", {
      state: { source: eventSource, trial: true },
    });
  };

  const handleNewsletter = () => {
    onUpgrade && onUpgrade();
    onClose && onClose();
    navigate("/profile/subscription", {
      state: { source: eventSource, newsletter: true },
    });
  };

  return (
    <Modal
      show={isVisible}
      onHide={onClose}
      centered
      backdrop={true}
      dialogClassName="upgrade-pro-modal"
      backdropClassName="upgrade-pro-modal-backdrop"
    >
      <Modal.Body
        ref={dialogRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        <div
          className="upm-handle"
          aria-hidden="true"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
        />

        <button
          type="button"
          className="upm-close"
          onClick={onClose}
          aria-label="Close"
        >
          <FiX size={20} />
        </button>

        <div className="upm-icon-row">
          <div className="upm-icon">
            <BuildingIcon className="upm-icon-svg" />
          </div>
          {showBadge && (
            <span className="upm-badge-limit">{DEFAULTS.badge}</span>
          )}
        </div>

        <h2 className="upm-title">{finalHeading}</h2>
        <p className="upm-sub">Upgrade to keep going.</p>

        <div className="upm-info">
          <SparkIcon className="upm-info-spark" />
          <p className="upm-info-text">{finalInfo}</p>
        </div>

        <div className="upm-ctas">
          {!hasUsedProTrial ? (
          <button
            type="button"
            className="upm-btn-primary"
            onClick={handleTrial}
          >
            <span className="upm-btn-primary-title">Start 3-day free trial</span>
            <span className="upm-btn-primary-sub">
              No charge today. {finalYearly} after your 3-day trial.
            </span>
          </button>
          ) : (
          <button
            type="button"
            className="upm-btn-primary"
            onClick={handleTrial}
          >
            <span className="upm-btn-primary-title">Get Pro</span>
            <span className="upm-btn-primary-sub">
              {finalTrial}, billed annually at {finalYearly}. Cancel anytime.
            </span>
          </button>
          )}

          <button
            type="button"
            className="upm-btn-secondary"
            onClick={handleSkipTrial}
            disabled={isCheckingOut}
          >
            {isCheckingOut
              ? "Opening Secure Payment..."
              : hasUsedProTrial
              ? `Buy Pro Plan · ${finalTrial}`
              : `Buy Pro Plan · ${finalTrial}`}
          </button>

          <button
            type="button"
            className="upm-newsletter"
            onClick={handleNewsletter}
          >
            <span className="upm-newsletter-q">
              Want jobs in your inbox too?
            </span>
            <span className="upm-newsletter-cta">
              Get {finalNewsletterLabel}, {finalNewsletterPrice}
            </span>
          </button>
        </div>

        <button type="button" className="upm-later" onClick={onClose}>
          Maybe later
        </button>
      </Modal.Body>
    </Modal>
  );
};

export default CityDetailLimitModal;
